import React from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', V3 = '#51158C', V4 = '#2B0057';
const CONFETTI_COLORS = [V1, V2, '#FFD166', '#2ECC71', '#3B82F6'];

// Deterministic pseudo-random spread so confetti looks scattered without re-randomizing on every render.
const CONFETTI = Array.from({ length: 26 }, (_, i) => {
  const left = (i * 37) % 100;
  const delay = ((i * 13) % 18) / 10;
  const duration = 2.6 + ((i * 7) % 14) / 10;
  const size = 6 + (i % 4) * 2;
  const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
  const rotate = (i * 47) % 360;
  const round = i % 3 === 0;
  return { left, delay, duration, size, color, rotate, round };
});

const STATS = [
  { n: '75', l: 'Curated Problems' },
  { n: '17', l: 'Core Patterns' },
  { n: '2', l: 'Companies Verified' },
];

const DsaWelcomeModal = ({ name, onClose }) => {
  const firstName = (name || '').trim().split(' ')[0] || 'there';

  return (
    <div className="dsa-welcome-overlay" onClick={onClose}>
      <div className="dsa-welcome-card" onClick={e => e.stopPropagation()}>
        <button className="dsa-welcome-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="dsa-welcome-confetti" aria-hidden="true">
          {CONFETTI.map((c, i) => (
            <span
              key={i}
              className="dsa-confetti-piece"
              style={{
                left: `${c.left}%`,
                width: c.size, height: c.size,
                background: c.color,
                borderRadius: c.round ? '50%' : 3,
                animationDelay: `${c.delay}s`,
                animationDuration: `${c.duration}s`,
                transform: `rotate(${c.rotate}deg)`,
              }}
            />
          ))}
        </div>

        <div className="dsa-welcome-badge">
          <span className="dsa-welcome-badge-ring" />
          <span className="dsa-welcome-badge-emoji">🚀</span>
        </div>

        <h2 className="dsa-welcome-title">
          You're in, <span className="dsa-welcome-title-grad">{firstName}!</span>
        </h2>
        <p className="dsa-welcome-body">
          75 problems stand between you and walking into your next interview knowing
          exactly what to do. Every pattern, cross-checked against real TCS and Infosys
          questions — not just famous ones.
        </p>

        <div className="dsa-welcome-stats">
          {STATS.map(s => (
            <div key={s.l} className="dsa-welcome-stat">
              <div className="dsa-welcome-stat-n">{s.n}</div>
              <div className="dsa-welcome-stat-l">{s.l}</div>
            </div>
          ))}
        </div>

        <button className="dsa-welcome-cta" onClick={onClose}>
          Let's Get Started →
        </button>
        <p className="dsa-welcome-hint">We'll ping you the moment the tracker goes live.</p>
      </div>

      <style>{`
        .dsa-welcome-overlay {
          position: fixed; inset: 0; z-index: 9100;
          background: rgba(10, 6, 20, 0.72);
          backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
          display: flex; align-items: center; justify-content: center; padding: 116px 20px 20px;
          animation: dsaFade 0.35s ease forwards;
        }
        .dsa-welcome-card {
          position: relative; width: 100%; max-width: 480px;
          background: linear-gradient(160deg, ${V4} 0%, ${V3} 55%, #1a0a33 100%);
          border: 1px solid rgba(177,99,255,0.35);
          border-radius: 26px;
          padding: 48px 36px 36px;
          text-align: center;
          color: #fff;
          overflow: hidden;
          box-shadow: 0 24px 80px -12px rgba(127,0,255,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset;
          animation: dsaPop 0.55s cubic-bezier(0.2, 1.1, 0.3, 1) forwards;
        }
        .dsa-welcome-close {
          position: absolute; top: 16px; right: 16px; z-index: 3;
          width: 32px; height: 32px; border-radius: 50%;
          background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.14);
          color: rgba(255,255,255,0.8); font-size: 13px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: background 0.2s;
        }
        .dsa-welcome-close:hover { background: rgba(255,255,255,0.18); }

        .dsa-welcome-confetti { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 1; }
        .dsa-confetti-piece { position: absolute; top: -14px; opacity: 0.9; animation-name: dsaFall; animation-timing-function: ease-in; animation-iteration-count: infinite; }

        .dsa-welcome-badge {
          position: relative; width: 84px; height: 84px; margin: 0 auto 22px;
          display: flex; align-items: center; justify-content: center; z-index: 2;
        }
        .dsa-welcome-badge-ring {
          position: absolute; inset: 0; border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%);
          animation: dsaPulseRing 2.2s ease-in-out infinite;
        }
        .dsa-welcome-badge-emoji {
          font-size: 40px; line-height: 1;
          filter: drop-shadow(0 4px 14px rgba(0,0,0,0.35));
          animation: dsaFloat 2.6s ease-in-out infinite;
        }

        .dsa-welcome-title {
          position: relative; z-index: 2;
          font-family: 'Inter', sans-serif;
          font-size: clamp(24px, 4vw, 30px); font-weight: 800; letter-spacing: -0.02em;
          margin: 0 0 14px; line-height: 1.2;
        }
        .dsa-welcome-title-grad {
          background: linear-gradient(135deg, #fff, ${V2});
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .dsa-welcome-body {
          position: relative; z-index: 2;
          font-size: 14.5px; line-height: 1.7; color: rgba(255,255,255,0.78);
          max-width: 380px; margin: 0 auto 26px;
        }

        .dsa-welcome-stats {
          position: relative; z-index: 2;
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
          margin-bottom: 28px;
        }
        .dsa-welcome-stat {
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
          border-radius: 14px; padding: 14px 8px;
        }
        .dsa-welcome-stat-n {
          font-family: 'Inter', sans-serif; font-weight: 800; font-size: 22px;
          background: linear-gradient(135deg, #fff, ${V2});
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .dsa-welcome-stat-l { font-size: 10.5px; color: rgba(255,255,255,0.6); margin-top: 4px; letter-spacing: 0.02em; }

        .dsa-welcome-cta {
          position: relative; z-index: 2;
          width: 100%; padding: 15px; border: none; border-radius: 13px; cursor: pointer;
          background: linear-gradient(135deg, #fff, #f1eaff);
          color: ${V1}; font-weight: 800; font-size: 15px;
          font-family: 'Satoshi', 'DM Sans', sans-serif;
          box-shadow: 0 10px 30px -6px rgba(0,0,0,0.4);
          transition: transform 0.2s ease;
        }
        .dsa-welcome-cta:hover { transform: translateY(-2px); }
        .dsa-welcome-hint {
          position: relative; z-index: 2;
          margin: 14px 0 0; font-size: 12px; color: rgba(255,255,255,0.5);
        }

        @keyframes dsaFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dsaPop {
          0% { opacity: 0; transform: scale(0.85) translateY(24px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes dsaFall {
          from { transform: translateY(-14px) rotate(0deg); opacity: 0.95; }
          to   { transform: translateY(420px) rotate(340deg); opacity: 0; }
        }
        @keyframes dsaPulseRing {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50%      { transform: scale(1.25); opacity: 1; }
        }
        @keyframes dsaFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }

        @media (prefers-reduced-motion: reduce) {
          .dsa-welcome-overlay, .dsa-welcome-card, .dsa-confetti-piece,
          .dsa-welcome-badge-ring, .dsa-welcome-badge-emoji { animation: none !important; }
        }
        @media (max-width: 520px) {
          .dsa-welcome-card { padding: 40px 22px 28px; border-radius: 22px; }
          .dsa-welcome-stats { gap: 8px; }
          .dsa-welcome-stat { padding: 12px 4px; }
        }
      `}</style>
    </div>
  );
};

export default DsaWelcomeModal;
